import { GetPointResponse, TagRequest, UserTag } from '../types';
import { LitElement, css, html } from 'lit';
import { cancelDefault, isDefined } from '../helpers/utils';
import { customElement, property, query, state } from 'lit/decorators';
import { dupeTagsExist, getPointsWithTags, moveItem } from '../helpers/tags-categories';
import { getThemeColors, getVisibleTextColor, makeColorIntoDataSvg } from '../helpers/colors';

import { DEFAULT_TAG_COLORS } from '../helpers/constants';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import { getMainPage } from '../helpers/page-name';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import { randomIntFromInterval } from '../helpers/math';
import topicData from '../helpers/state/topic-data';

@customElement('tag-form')
export class TagForm extends LitElement {
    amplitude_callsite: string = 'Tag Form';

    @query('#colorPicker')
    private colorPickerElement!: HTMLInputElement;

    @query('#subtagName')
    private readonly subtagName?: TextField;

    @property({ type: Object })
    tag?: UserTag;

    @property({ type: Boolean })
    showSubTags: boolean = false; // this is true if we are creating a tag group

    @property({ type: String })
    icon: string = DEFAULT_TAG_COLORS[randomIntFromInterval(0, DEFAULT_TAG_COLORS.length)];

    @state()
    renderableIconString: string = this.icon?.startsWith('#') ? makeColorIntoDataSvg(this.icon) : this.icon;

    @state()
    pointsToUpdate: GetPointResponse[] = []; // you need to update the points that have this tag when editing the tag

    @state()
    showSubTagInput: boolean = false;

    @state()
    activeSubtag?: UserTag; // if we are editing a subtag, this is the subtag we are editing

    @state()
    suggestedColors: string[] = [];

    @state()
    pickedColor: string = '';

    @state()
    showColors: boolean = false;

    @state()
    isReordering: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);

    updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {

            if(propName === 'tag' && isDefined(this.tag)) {
                this.pointsToUpdate = getPointsWithTags([this.tag, ...(this.tag?.tags ?? [])], pointCache.points);
                this.icon = this.tag.icon === '' ? DEFAULT_TAG_COLORS[randomIntFromInterval(0, DEFAULT_TAG_COLORS.length)] : this.tag.icon;
            }
            if (propName === 'icon') {
                this.renderableIconString = this.icon?.startsWith('#') ? makeColorIntoDataSvg(this.icon) : this.icon;
            }
        });

        // if showSubTagInput is changed, send dispatch to parent
        this.dispatchEvent(new CustomEvent('show-subtag-input', { detail: { showSubTagInput: this.showSubTagInput } }));
    }

    protected firstUpdated() {
        this.setupPalette();
    }

    async onChangeTagName(e: CustomEvent) {
        const name = (e.target as HTMLInputElement).value.trim();
        if (this.tag?._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag ID is undefined');
            return;
        }

        const dupeTag = dupeTagsExist(name, topicData.allUserTags);

        if (dupeTag !== undefined) {
            getMainPage().errorToastElement.showToastAndThrow(`There is already a tag named ${dupeTag}. Please enter a new tag name.`);
        }

        await topicData.fetchAndUpdateUserTag(this.tag?._id?.$oid, {
            name,
        }, this.amplitude_callsite);
        this.dispatchEvent(new CustomEvent('points-updating', { detail: { pointsUpdating: true } }));
        await pointCache.syncPointTags(this.pointsToUpdate, topicData.topLevelUserTags);
        this.dispatchEvent(new CustomEvent('points-updating', { detail: { pointsUpdating: false } }));
    }

    async onChangeTagIconColor(iconColor: string) {
        this.icon = iconColor;

        this.pickedColor = iconColor;

        if (this.tag?._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag ID is undefined');
            return;
        }

        await topicData.fetchAndUpdateUserTag(this.tag?._id?.$oid, {
            icon: iconColor,
        }, this.amplitude_callsite);

        this.showColors = false;
    }

    private openColorPicker() {
        this.colorPickerElement.focus();
        this.colorPickerElement.value = this.icon ?? '';
        this.colorPickerElement.click();

        logWithAmplitude(this.amplitude_callsite, 'Open Color Picker');
    }

    async showAddSubtag() {
       await this.addSubtag();
    }

    async addSubtag() {
        if (this.tag?._id) {
            const newSubtag = await topicData.createNewUserSubTag(this.tag?._id?.$oid, this.amplitude_callsite);
            this.showSubTagInput = true;
            this.activeSubtag = newSubtag;
            this.fetchUpdatedTag();
        }
    }

    async deleteSubtag(subtag: UserTag) {
        if (this.tag?._id === undefined || subtag._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag or Subtag ID is undefined');
            return;
        }
        await topicData.deleteSubTag(this.tag._id.$oid, subtag._id.$oid, this.amplitude_callsite);

        this.dispatchEvent(new CustomEvent('points-updating', { detail: { pointsUpdating: true } }));
        await pointCache.syncPointTags(this.pointsToUpdate, topicData.topLevelUserTags);
        this.dispatchEvent(new CustomEvent('points-updating', { detail: { pointsUpdating: false } }));

        this.fetchUpdatedTag();
    }

    private fetchUpdatedTag() {
        if (this.tag?._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag ID is undefined');
        }
        this.tag = topicData.topLevelUserTags.find((tag) => tag._id?.$oid === this.tag?._id?.$oid);
        this.requestUpdate();
    }

    async openRenameSubtag(subtag: UserTag) {
        this.showSubTagInput = true;
        this.activeSubtag = subtag;

        await this.updateComplete;
        if (isDefined(this.subtagName)) {
            this.subtagName.value = subtag.name;
        }
    }

    async renameSubtag(newName: string) {
        const subtag = this.activeSubtag;
        // if this function is being called, there will always be subtags, but need to check for compiler
        if (!isDefined(this.tag) || !isDefined(this.tag.tags) || !isDefined(subtag)) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag or Subtag is undefined');
            return;
        }

        const dupeTag = dupeTagsExist(newName, topicData.allUserTags);

        if (dupeTag !== undefined) {
            getMainPage().errorToastElement.showToastAndThrow(`There is already a tag named ${dupeTag}. Please enter a new tag name.`);
        }

        if (this.tag?._id === undefined || subtag._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag or Subtag ID is undefined');
            return;
        }

        await topicData.fetchAndUpdateUserSubTag(this.tag._id.$oid, subtag._id.$oid, {
            name: newName,
        }, this.amplitude_callsite);

        this.dispatchEvent(new CustomEvent('points-updating', { detail: { pointsUpdating: true } }));
        await pointCache.syncPointTags(this.pointsToUpdate, topicData.topLevelUserTags);
        this.dispatchEvent(new CustomEvent('points-updating', { detail: { pointsUpdating: false } }));

        this.activeSubtag = undefined;
        this.showSubTagInput = false;

        this.fetchUpdatedTag();

        this.requestUpdate();
    }

    async saveReorderedSubTags(subtags: UserTag[]) {
        if (this.tag?._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag ID is undefined');
            return;
        }

        this.tag.tags = subtags;
        await topicData.fetchAndUpdateUserTag(this.tag._id.$oid, {
            tags: subtags,
        }, this.amplitude_callsite);
        this.isReordering = false;
    }

    setToReordering() {
        this.isReordering = true;
    }


    private setupPalette() {
        this.suggestedColors = getThemeColors();
    }


    async bulkAddSubTags(e: CustomEvent) {

        const input = prompt(`Enter comma separated values for name of new sub-tags under ${this.tag?.name ?? 'the tag'}. This will create sub-tags under the selected tag.`);
        if (input && this.tag) {
            const values = input.split(',').map(item => item.trim());
            // Call a function to handle the bulk addition of sub-tags
            await this.handleBulkAddSubTags(values, this.tag);
        }
    }

    async handleBulkAddSubTags(values: string[], parentTag: UserTag) {
        const newSubTags: TagRequest[] = [];
        values.forEach(value => {
            const newSubTag: TagRequest = {
                _id: undefined,
                name: value,
                tags: [],
                icon: parentTag.icon,
            };
            newSubTags.push(newSubTag);
        });
        const request: Partial<TagRequest> = {
            tags: [...(parentTag.tags ?? []), ...newSubTags],
        };
        const userTagId = this.tag?._id?.$oid;
        if (!userTagId) {
            console.error('No valid userTagId for bulk addition of sub-tags.');
            return;
        }
        await topicData.fetchAndUpdateUserTag(userTagId, request, this.amplitude_callsite);
    }


    render() {
        const tag = topicData.topLevelUserTags.find((tag) => tag._id?.$oid === this.tag?._id?.$oid);
        return html`
            <span class="edit__inputs-container">
                <span class="edit__inputs">
                    <proxi-textfield outlined id="name" class="edit__textfield" label="Tag ${this.showSubTags ? 'Group' : ''} Name" @change=${this.onChangeTagName} value="${tag?.name ?? ''}" charcounter minlength="1" maxlength="35" required></proxi-textfield>

                    ${this.showSubTags ?
                        html`<label for="subtag" class="edit__label">Add Sub-Tags to this Group <br>
                                <span style="color: var(--main-primary-color); cursor: pointer; font-weight: 400; text-decoration: underline;"
                                    @click="${this.bulkAddSubTags}"><i>(Bulk Add Sub Tags)</i></span></label>
                                <span style="display: flex; flex-wrap: wrap;">
                                        ${tag?.tags?.map(
                                            (tag, index) => html`
                                                <div draggable="true"
                                                    @dragstart="${(e: DragEvent) => this.handleDragStart(e)}"
                                                    @drop="${async (e: DragEvent) => await this.handlePillDrop(e)}"
                                                    @dragenter="${(e: DragEvent) => this.handleDragEnter(e)}"
                                                    @dragleave="${(e: DragEvent) => this.handleDragLeave(e)}"
                                                    @dragend="${(e: DragEvent) => this.handleDragEnd(e)}"
                                                    @dragover="${(e: DragEvent) => cancelDefault(e)}"
                                                    index="${index}"
                                                    class="edit__tag-group-item ${this.activeSubtag?._id?.$oid === this.tag?._id?.$oid ? 'active-subtag' : ''}" style="background-color: ${tag.icon}; color: ${getVisibleTextColor(tag.icon)}">
                                                    <mwc-icon-button class="edit__delete-tag-group-item" icon="edit" @click="${async () => await this.openRenameSubtag(tag)}"></mwc-icon-button>
                                                    ${tag.name}
                                                    <mwc-icon-button class="edit__delete-tag-group-item" icon="delete" @click="${async() => await this.deleteSubtag(tag)}"></mwc-icon-button>
                                                </div>
                                            `
                                        )}
                                        ${this.showSubTagInput ? '' : html`
                                            <div class="edit__tag-group-item" @click="${this.showAddSubtag}" >
                                                <mwc-icon-button class="edit__delete-tag-group-item" icon="add"></mwc-icon-button>
                                                Add Sub-Tag
                                            </div>
                                        `}
                                    </span>
                                </span>`
                        : ''
                    }
                </span>
                <div class="edit__icons" @click="${() => {this.showColors = !this.showColors;}}">
                    <span class="edit__icon">
                        <img class="edit__icon-image-main" src="${this.renderableIconString}">
                        <span class="edit__icon-image">
                            Edit
                        </span>
                    </span>
                </div>
            </span>
            ${this.showColors ? html`
                <div class="palette-wrapper">
                    <div class="palette-color" style="background-color: white;" @click="${this.openColorPicker}">
                        <div class="active-status">
                            <mwc-icon style="color: black">colorize</mwc-icon>
                        </div>
                    </div>
                    ${this.suggestedColors.slice(0, 15).map((paletteColor) => html`
                        <div
                            class="palette-color"
                            style="background-color: ${paletteColor};"
                            @click="${async() => { await this.onChangeTagIconColor(paletteColor); }}">
                            <div class="active-status">
                                ${this.pickedColor.toLowerCase() === paletteColor.toLowerCase() ? html`
                                    <mwc-icon>check</mwc-icon>
                                ` : ''}
                            </div>
                        </div>
                    `)}
                </div>
                <input id="colorPicker" type="color" @blur="${async(e: any) => { await this.onChangeTagIconColor(e.target.value); } }"/>
            ` : html`
                ${this.showSubTagInput && this.activeSubtag ?
                    html`
                        <div style="display: flex; align-items: center">
                            <proxi-textfield outlined id="subtagName" class="edit__textfield" label="Sub-Tag name" charcounter minlength="1" maxlength="35" @change="${async (e: any) => { await this.renameSubtag(e.target.value); }}" required>
                            </proxi-textfield>
                        </div>`
                    : ''
                }
            `}
            <error-toast id="errorToast"></error-toast>

        `;
    }

    handleDragStart(e: any) {
        e.target.style.opacity = '0.4';
        e.dataTransfer?.setData('text', e.target?.getAttribute('index'));
        e.dataTransfer.dropEffect = 'move';
    }

    handleDragEnter(e: any) {
        e.target.classList.add('over');
        cancelDefault(e);
    }

    handleDragLeave(e: any) {
        e.target.classList.remove('over');
        cancelDefault(e);
    }

    handleDragEnd(e: any) {
        e.target.style.opacity = '1';
        cancelDefault(e);
    }

    async handlePillDrop(e: any) {
        if (this.tag?.tags === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: unable to reorder subtags');
        }
        e.target.classList.remove('over');

        const droppedElementIndex = e.dataTransfer?.getData('text');
        const newElementIndex = e.target.getAttribute('index');
        const reorderedData = moveItem(this.tag?.tags ?? [], droppedElementIndex, newElementIndex);
        if (this.tag?._id === undefined) {
            getMainPage().errorToastElement.showToastAndThrow('Error: Tag ID is undefined');
            return;
        }
        await topicData.fetchAndUpdateUserTag(this.tag?._id?.$oid, {
            tags: reorderedData,
        }, this.amplitude_callsite);

        cancelDefault(e);
    }

    static get styles() {
        return css`
            .edit__inputs-container {
                display: flex;
                gap: 1rem;
                margin-bottom: 1rem;
            }

            .edit__inputs {
                flex: 1;
            }

            .edit__textfield {
                width: 100%;
                margin-top: 4px;
            }

            .edit__dialog {
                --mdc-dialog-min-width: 360px;
            }

            .edit__icons {
                margin-left: 8px;
            }

            .edit__icon {
                display: flex;
                font-size: 13px;
                flex-direction: column;
                align-items: center;
                text-decoration:underline;
            }

            .edit__icon:not(.edit__tag) {
                margin-bottom: 12px;
            }

            .edit__icon.edit__tag {
                flex-direction: column;
            }

            .edit__icon-image {
                padding-right: 4px;
                --mdc-icon-size: 18px;
                --mdc-icon-button-size: 18px;
            }

            .edit__icon-image-main {
                padding-right: 4px;
                margin-left: auto;
                margin-right: auto;
                height: 32px;
                width: 32px;
            }

            #colorPicker {
                opacity: 0;
                height: 0;
                width: 0;
                margin: 0;
            }

            .icon__actions {
                padding-top: 18px;
                display: flex;
                justify-content: space-evenly;
            }

            .active-subtag {
                border: 1px solid var(--main-primary-color);
            }

            .edit__tag-group-wrapper {
                display: flex;
                flex-wrap: wrap;
                margin-bottom: 1rem;
            }

            .edit__tag-group-item {
                background: white;
                width: fit-content;
                border: 1px solid lightgray;
                border-radius: 10px;
                padding: 8px;
                margin: 4px 4px 4px 0px;
                cursor: pointer;
                filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
                height: fit-content;
            }

            .edit__tag-group-item-icon {
                --mdc-icon-size: 14px;
                vertical-align: text-bottom;
            }

            .edit__delete-tag-group-item {
                --mdc-icon-size: 16px;
                --mdc-icon-button-size: 16px;
            }

            .palette-wrapper {
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                margin: 1rem -0.25rem 0.25rem
            }

            .palette-color {
                flex: 1;
                margin: 0.25rem;
                border-radius: 50%;
                min-width: calc(12.5% - 0.5rem);
                padding-bottom: calc(12.5% - 0.5rem);
                position: relative;
                cursor: pointer;
                box-sizing: border-box;
                box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
            }

            .active-status {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                color: white;
            }
        `;
    }

}
